import React, { lazy, useCallback, useMemo, Suspense, useRef, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { motion } from 'framer-motion';
import { Spin, Button, Typography, Skeleton, Tag, Modal } from 'antd';

import UIStore from '@/stores/UIStore';
import {
    DeleteOutlined,
    RightOutlined,
    LoadingOutlined,
    ArrowLeftOutlined,
    ArrowRightOutlined,
    ClockCircleOutlined,
    DownOutlined,
} from '@ant-design/icons';

import { MaterialProps, FinishesProps, CoatingsProps, QuoteLineItemProps, QuoteProps } from '@partsbadger/types';
import {
    PartInfoCollapsed,
    PartInfoExpanded,
    StepsNavigator,
    // ProcessStep,
    LeadTimeSection,
    FrequencySelector,
    // MaterialStep,
    // FinishStep,
    // CoatingStep,
} from '../../';

import {
    ManualQuoteAlert,
    CalculatingPriceState,
    QuantitySelector,
    ShortLeadTimeOptions,
} from '@partsbadger/common-features';
import { LeadTimePriceOptions } from '../../LeadTimeOptions';
import { Price } from '@partsbadger/library';
import { isConfiguredPartComplete } from '../../../customOptionValidation';

import StepRenderer from '../StepRenderer';
import Divider from '@/components/Divider';

const { Text } = Typography;

interface PartRowProps {
    part: QuoteLineItemProps;
    /** When true, reduces top margin for the first card in the list */
    isFirstPart?: boolean;

    isExpanded: boolean;
    isVariation: boolean;
    isActive: boolean;
    showError: boolean;
    isHobbyist: boolean;

    /** step handling */
    currentStep: number;
    setStep: (partId: number, step: number) => void;

    /** state & flags */
    loadingPartId: number | null;
    isRapidQuote: boolean;
    isRequote: boolean;
    quoteToken: string;

    /** data */
    materialsList: MaterialProps[];
    topMaterials: MaterialProps[];
    finishesList: FinishesProps[];
    coatingsList: CoatingsProps[];
    topCoatings: CoatingsProps[];

    /** actions */
    togglePartExpansion: (partId: number, e?: React.MouseEvent) => void;

    handleSubmit: (payload: any) => void;
    onUpdateItemPrice: (partId: number, leadTimeOptionId: string) => void;
    onDuplicateItem: (masterProductId: number) => void;
    onRemoveMasterProduct: (masterProductId: number) => void;
    onRemoveVariation: (uid: string) => void;
    onPendingCustomChange?: (partId: number, stepIndex: number, isPending: boolean) => void;

    /** refs */
    partRefs: React.MutableRefObject<Record<number, HTMLDivElement | null>>;

    variantName: string | null;
}

const PartRow = function PartRow({
    variantName,
    part,
    isFirstPart = false,
    isExpanded,
    isVariation,
    isActive,
    isHobbyist,
    showError,
    currentStep,
    setStep,

    loadingPartId,
    isRapidQuote,
    isRequote,
    quoteToken,

    materialsList,
    topMaterials,
    finishesList,
    coatingsList,
    topCoatings,

    togglePartExpansion,

    handleSubmit,
    onUpdateItemPrice,
    onDuplicateItem,
    onRemoveMasterProduct,
    onRemoveVariation,
    onPendingCustomChange,

    partRefs,
}: PartRowProps) {
    const { pricing_options, quantity, lead_time, product } = part;

    const leadTimeEntries = useMemo(() => pricing_options?.prices_by_quantity?.[quantity] ?? [], [
        pricing_options,
        quantity,
    ]);

    const selectedLeadTime = useMemo(() => leadTimeEntries.find(o => o.lead_time === lead_time) ?? null, [
        leadTimeEntries,
        lead_time,
    ]);

    const totalPrice = selectedLeadTime?.total_price ?? 0;
    const unitPrice = selectedLeadTime?.unit_price ?? 0;

    const isComplete = isConfiguredPartComplete(part);

    const isSafeAutoQuote = part.safe_auto_quote && !part.needs_manual_quote;

    // Geometry is ready but the async pricing analysis has not concluded yet.
    // Show a transitional "Calculating prices…" state instead of flashing the
    // manual-quote fallback.
    const isPricingInProgress = !isSafeAutoQuote && Boolean(part.is_pricing_in_progress);

    const { analysis_status } = part?.product || {};
    const analyzer_2d_finished = analysis_status.file_2d?.completed;
    const analyzer_3d_finished = analysis_status.file_3d?.completed;
    // The 2D analyzer only enriches optional material/finish/coating
    // suggestions. It must not block the rest of the quote form.
    const isAnalyzingSuggestions = product.has_file_2d && !analyzer_2d_finished;
    const isAnalyzing = product.has_file_3d && !analyzer_3d_finished;
    const primaryAnalysisFinished = Boolean(analyzer_3d_finished);

    const steps = [
        { id: 0, name: 'Process', component: 'process' },
        { id: 1, name: 'Material', component: 'material' },
        { id: 2, name: 'Surface Finish', component: 'surface' },
        { id: 3, name: 'Coating', component: 'coating' },
    ];

    const isLoading = loadingPartId === part.id;

    const [pendingCustomByStep, setPendingCustomByStep] = useState<Record<number, boolean>>({});
    const handlePendingCustomChange = useCallback(
        (stepIndex: number, isPending: boolean) => {
            setPendingCustomByStep(prev => ({ ...prev, [stepIndex]: isPending }));
            onPendingCustomChange?.(part.id, stepIndex, isPending);
        },
        [onPendingCustomChange, part.id]
    );
    const isBlockedForward = Boolean(pendingCustomByStep[currentStep]);

    // When the row/modal closes, MaterialStep (etc.) unmounts before it can report pending=false.
    // That left stale pending flags and blocked step navigation (e.g. Coating) via isBlockedForward.
    useEffect(() => {
        if (!isExpanded) {
            setPendingCustomByStep({});
        }
    }, [isExpanded]);

    const isTabletOrLess = UIStore.isMobileViewport;

    // Sync Caret Position with Step Navigator
    const stepsContainerRef = useRef<HTMLDivElement>(null);
    const caretRef = useRef<HTMLDivElement>(null);
    const stepRefs = useRef<Record<number, HTMLButtonElement | null>>({});

    // When part expands or step changes, scroll to top only if the part is not already in view (avoids jarring scroll)
    useEffect(() => {
        if (!isExpanded) return;
        const el = partRefs.current[part.id];
        if (!el) return;
        const timeoutId = setTimeout(() => {
            const rect = el.getBoundingClientRect();
            const scrollMarginTop = 16;
            const tolerance = 24;
            const desiredTop = scrollMarginTop;
            const isAlreadyInPlace = rect.top >= desiredTop - tolerance && rect.top <= desiredTop + tolerance;
            if (!isAlreadyInPlace) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
            }
        }, 120);
        return () => clearTimeout(timeoutId);
    }, [isExpanded, currentStep, part.id]);

    useEffect(() => {
        if (!isExpanded) {
            return;
        }

        const stepEl = stepRefs.current[currentStep];
        const caretEl = caretRef.current;

        if (!stepEl || !caretEl) {
            return;
        }

        const caretContainer = caretEl.offsetParent as HTMLElement | null;
        if (!caretContainer) {
            return;
        }

        const updateCaretPosition = () => {
            const stepRect = stepEl.getBoundingClientRect();
            const containerRect = caretContainer.getBoundingClientRect();

            const stepCenter = stepRect.left + stepRect.width / 2;
            const containerCenter = containerRect.left + containerRect.width / 2;

            let centerX = stepCenter - containerRect.left;

            // On small screens, nudge based on how far the step is from the container center,
            // not a fixed 70/90px.
            if (typeof window !== 'undefined' && window.innerWidth < 640) {
                const delta = stepCenter - containerCenter; // >0: step is to the right, <0: to the left
                const bias = delta * 0.3; // 30% of that distance (tune 0.3 as needed)

                centerX -= bias;
            }

            caretEl.style.transform = `
                    translateX(${centerX}px)
                    translateX(-50%)
                    translateY(calc(-50% - 1px))
                    rotate(45deg)
                `;
        };

        updateCaretPosition();
        const rafId = requestAnimationFrame(updateCaretPosition);

        return () => cancelAnimationFrame(rafId);
    }, [currentStep, isExpanded, part]);

    const handleRemove = async () => {
        Modal.confirm({
            title: 'Remove Part',
            content: 'Are you sure you want to remove this part?',
            okText: 'Remove',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                await Promise.resolve(onRemoveMasterProduct(part?.product?.master_product));
            },
        });
    };

    const StepSkeleton = () => (
        <div className="p-4">
            <Skeleton active paragraph={{ rows: 4 }} title />
        </div>
    );

    const handleSubmitAndCloseModal = useCallback(
        (payload: Parameters<typeof handleSubmit>[0]) => {
            handleSubmit(payload);
            const p = payload as Record<string, unknown>;
            const product = (p.product as Record<string, unknown> | undefined) ?? p;
            const noCustomValue = (v: unknown) => v == null || (typeof v === 'string' && v.trim() === '');

            // TODO: This is a hack to check if the custom material is without value. We need to improve this.

            const isCustomMaterialWithoutValue =
                product.material != null &&
                noCustomValue(product.custom_material) &&
                materialsList?.find((m: { id: number; name?: string }) => m.id === product.material)?.name === 'Custom';
            const finishes = product.finishes as number[] | undefined;
            const coatings = product.coatings as number[] | undefined;
            const isCustomFinishWithoutValue =
                Array.isArray(finishes) &&
                finishes.length === 1 &&
                noCustomValue(product.custom_finish) &&
                finishesList?.find((f: { id: number; name?: string }) => f.id === finishes[0])?.name === 'Custom';
            const isCustomCoatingWithoutValue =
                Array.isArray(coatings) &&
                coatings.length === 1 &&
                noCustomValue(product.custom_coating) &&
                coatingsList?.find((c: { id: number; name?: string }) => c.id === coatings[0])?.name === 'Custom';

            if (!isCustomMaterialWithoutValue && !isCustomFinishWithoutValue && !isCustomCoatingWithoutValue) {
                setPendingCustomByStep({});
                togglePartExpansion(part.id);
            }
        },
        [handleSubmit, togglePartExpansion, part.id, materialsList, finishesList, coatingsList]
    );

    const modalStepContent = (
        <div className="panel-container px-4 overflow-x-auto">
            <div className="flex-1 min-h-0 overflow-y-auto p-4">
                <Suspense fallback={<StepSkeleton />}>
                    <div className="step-item">
                        <StepRenderer
                            currentStep={currentStep}
                            part={part}
                            isHobbyist={isHobbyist}
                            materialsList={materialsList}
                            topMaterials={topMaterials}
                            finishesList={finishesList}
                            coatingsList={coatingsList}
                            topCoatings={topCoatings}
                            isLoading={isLoading}
                            suggestionsLoading={isAnalyzingSuggestions}
                            handleSubmit={handleSubmitAndCloseModal}
                            onPendingCustomChange={handlePendingCustomChange}
                            onAdvanceStep={() => {
                                if (currentStep === 0) {
                                    setStep(part.id, 1);
                                }
                            }}
                        />
                    </div>
                </Suspense>
            </div>
        </div>
    );

    const stepPanelContent = (
        <div className="panel-container px-4 overflow-x-auto">
            <div className="grid grid-cols-1 parts-config-grid ">
                <div className="lg:col-span-2">
                    <div
                        className="bg-white rounded-lg flex flex-col mb-2 min-h-0"
                        style={{ maxHeight: isTabletOrLess ? '70vh' : '75vh' }}
                    >
                        <div className="flex-1 min-h-0 overflow-y-auto p-4">
                            <Suspense fallback={<StepSkeleton />}>
                                <div className="step-item">
                                    <StepRenderer
                                        currentStep={currentStep}
                                        part={part}
                                        isHobbyist={isHobbyist}
                                        materialsList={materialsList}
                                        topMaterials={topMaterials}
                                        finishesList={finishesList}
                                        coatingsList={coatingsList}
                                        topCoatings={topCoatings}
                                        isLoading={isLoading}
                                        suggestionsLoading={isAnalyzingSuggestions}
                                        handleSubmit={handleSubmit}
                                        onPendingCustomChange={handlePendingCustomChange}
                                        onAdvanceStep={() => {
                                            if (currentStep === 0) {
                                                setStep(part.id, 1);
                                            }
                                        }}
                                    />
                                </div>
                            </Suspense>
                        </div>
                        <div className="step-button-container shrink-0 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 bg-white border-t border-slate-200">
                            {currentStep > 0 ? (
                                <Button
                                    type="default"
                                    size="small"
                                    data-testid="step-nav-back"
                                    onClick={() => setStep(part?.id, currentStep - 1)}
                                    className="flex items-center space-x-2 w-full sm:w-auto"
                                    style={{ borderColor: '#c00', color: '#c00' }}
                                >
                                    <div className="flex items-center justify-center">
                                        <ArrowLeftOutlined className="w-4 h-4" />
                                        <span className="hidden sm:inline ml-2">
                                            {{ 1: 'Process', 2: 'Material', 3: 'Surface Finish' }[currentStep] || ''}
                                        </span>
                                    </div>
                                </Button>
                            ) : (
                                <Button
                                    type="default"
                                    size="small"
                                    data-testid="step-nav-back"
                                    disabled
                                    className="flex items-center justify-center w-full sm:w-auto"
                                    style={{ minWidth: 40 }}
                                >
                                    <ArrowLeftOutlined className="w-4 h-4 text-slate-300" />
                                </Button>
                            )}
                            <div className="text-xs sm:text-sm text-slate-500 text-center sm:text-left order-2 sm:order-none">
                                Step {(currentStep || 0) + 1} of {steps.length}
                            </div>
                            <Button
                                size="small"
                                type="ghost"
                                data-testid="step-nav-forward"
                                style={{ borderColor: '#c00', color: '#c00' }}
                                disabled={isBlockedForward}
                                onClick={() => {
                                    if (isBlockedForward) return;
                                    if (currentStep < 3) {
                                        setStep(part?.id, currentStep + 1);
                                    } else {
                                        togglePartExpansion(part.id);
                                    }
                                }}
                                className="flex items-center space-x-2 w-full sm:w-auto"
                            >
                                <div className="flex items-center justify-center">
                                    {{ 0: 'Material', 1: 'Surface Finish', 2: 'Coating' }[currentStep] || 'Done'}
                                    {(currentStep || 0) < 3 ? (
                                        <ArrowRightOutlined className="w-4 h-4 ml-0 sm:ml-2" />
                                    ) : null}
                                </div>
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="lg:col-span-1 mt-10 lg:mt-0">
                    <div
                        id={`quantity-section-${part.id}`}
                        className="bg-white rounded-lg sticky top-4 flex flex-col min-h-0"
                        style={{ maxHeight: isTabletOrLess ? '70vh' : '75vh' }}
                    >
                        <div
                            className="p-3 border-b border-slate-200 shrink-0"
                            style={{ backgroundColor: 'rgb(247, 249, 252)' }}
                        >
                            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                                <ClockCircleOutlined className="w-5 h-5 text-blue-600" />
                                <span>Quantity & Lead Time</span>
                            </h3>
                        </div>
                        <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
                            <div className="flex flex-col md:flex-row gap-2 justify-between items-end">
                                <QuantitySelector
                                    quantity={part?.quantity}
                                    onQuantitySubmit={quantity => handleSubmit({ id: part.id, quantity })}
                                    loading={isLoading}
                                    min={1}
                                    labelPosition="top"
                                    labelClassName="text-sm font-semibold text-slate-800 mb-2"
                                />
                                <FrequencySelector
                                    value={part.quantity_frequency || 'one-time'}
                                    onChange={value => handleSubmit({ id: part.id, quantity_frequency: value })}
                                    disabled={isLoading}
                                />
                            </div>
                            {!isSafeAutoQuote &&
                                (isPricingInProgress ? (
                                    <CalculatingPriceState />
                                ) : (
                                    <ManualQuoteAlert pricingFeedback={part.customer_pricing_feedback} />
                                ))}
                            <LeadTimeSection
                                part={part}
                                loading={isLoading}
                                onUpdateItemPrice={leadTimeOptionId => {
                                    if (leadTimeOptionId) {
                                        onUpdateItemPrice(part.id, leadTimeOptionId);
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div
            key={part.id}
            // layout="position"
            // initial={false}
            data-testid="part-row"
            data-part-id={part.id}
            data-part-analysis-state={isAnalyzing ? 'pending' : primaryAnalysisFinished ? 'ready' : 'unknown'}
            data-current-step={currentStep}
            data-expanded={isExpanded ? 'true' : 'false'}
            data-loading={isLoading ? 'true' : 'false'}
            className={`part-item-main-container mt-2 mb-2 ${isVariation ? 'ml-8' : ''}`}
            ref={el => {
                partRefs.current[part.id] = el;
            }}
            style={{
                marginTop: isFirstPart ? 4 : 16,
                scrollMarginTop: 16,
            }}
        >
            <div
                className={`relative rounded-lg border-2 transition-all ${
                    showError
                        ? 'border-red-500 bg-red-50'
                        : isActive
                        ? 'border-blue-500'
                        : isVariation
                        ? ' bg-white'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
            >
                {/* Part Header - Always Visible */}

                <div>
                    {isAnalyzing ? (
                        <>
                            <div className="absolute z-10" style={{ top: '8px', right: '8px' }}>
                                <Button
                                    size="small"
                                    type="text"
                                    onClick={() => handleRemove()}
                                    icon={<DeleteOutlined className="" />}
                                    className="h-6 w-6 p-0  hover:text-red-700 hover:bg-red-50"
                                    title="Remove"
                                    disabled={false}
                                />
                            </div>

                            <div className="loader pb-4 pt-4" title="Geometry Analyzing">
                                <Spin
                                    indicator={
                                        <>
                                            <LoadingOutlined style={{ fontSize: 24 }} spin />
                                            <Text className="d-block text-primary">Analyzing Geometry...</Text>
                                            <Text type="secondary" className="d-block mt-1" style={{ fontSize: 13 }}>
                                                {part?.name
                                                    ? `Part: ${part.name}`
                                                    : part?.product?.attachments?.file3d?.filename || 'Your part'}
                                            </Text>
                                        </>
                                    }
                                    style={{ display: 'block' }}
                                />
                            </div>
                        </>
                    ) : (
                        primaryAnalysisFinished && (
                            <>
                                <div className="part-item pt-4 pb-4 px-4">
                                    <PartInfoCollapsed
                                        quoteToken={quoteToken}
                                        part={part}
                                        togglePartExpansion={id => {}}
                                        onDuplicate={onDuplicateItem}
                                        onRemove={isVariation => {
                                            if (isVariation) {
                                                onRemoveVariation(part?.uid);
                                                return;
                                            }
                                            onRemoveMasterProduct(part?.product?.master_product);
                                        }}
                                        onNameChange={(id, name) => console.log(id, name)}
                                        loading={isLoading}
                                        isVariation={isVariation}
                                        variantName={variantName}
                                        isExpanded={isExpanded}
                                        onChange={(value: Record<string, unknown>) => {
                                            handleSubmit({
                                                id: part.id,
                                                ...value,
                                            });
                                        }}
                                        isHobbyist={isHobbyist}
                                    />

                                    <div className="step-navigator w-full  p-2">
                                        <div className="flex items-center gap-1">
                                            <StepsNavigator
                                                stepRefs={stepRefs}
                                                isExpanded={isExpanded}
                                                steps={steps}
                                                part={part}
                                                singleColumn={isTabletOrLess}
                                                onStepClick={(partId, stepId) => {
                                                    if (stepId > currentStep && isBlockedForward) return;
                                                    setStep(part?.id, stepId);
                                                    if (currentStep == stepId || !isExpanded) {
                                                        togglePartExpansion(part.id);
                                                    }
                                                }}
                                                currentStep={currentStep}
                                            />
                                        </div>
                                    </div>

                                    {/* Bottom Row: Steps + Price/Status + Expand Button */}
                                    <div className="flex items-start justify-between gap-3">
                                        {isExpanded ? (
                                            /* When Expanded: New layout with improved information density */
                                            <>
                                                {/*<PartInfoExpanded*/}
                                                {/*    part={part}*/}
                                                {/*    quoteToken={quoteToken}*/}
                                                {/*    isRequote={isRequote}*/}
                                                {/*    reanalyzeMasterProduct={id => console.log(id)}*/}
                                                {/*    onChange={(value: Record<string, unknown>) => {*/}
                                                {/*        handleSubmit({*/}
                                                {/*            id: part.id,*/}
                                                {/*            ...value,*/}
                                                {/*        });*/}
                                                {/*    }}*/}
                                                {/*    onRemove={isVariation => {*/}
                                                {/*        if (isVariation) {*/}
                                                {/*            onRemoveVariation(part?.uid);*/}
                                                {/*            return;*/}
                                                {/*        }*/}
                                                {/*        onRemoveMasterProduct(part?.product?.master_product);*/}
                                                {/*    }}*/}
                                                {/*    loading={isLoading}*/}
                                                {/*    onDuplicateItem={onDuplicateItem}*/}
                                                {/*    isVariation={isVariation}*/}
                                                {/*    isRapidQuote={isRapidQuote}*/}
                                                {/*    variantName={variantName}*/}
                                                {/*    togglePartExpansion={() => togglePartExpansion(part.id)}*/}
                                                {/*    isExpanded={isExpanded}*/}
                                                {/*/>*/}

                                                {/* Steps Section Below - Full Width */}
                                            </>
                                        ) : (
                                            /* When Collapsed: Show steps horizontally */
                                            <div className="flex-1 min-w-0">
                                                {/* Step Progress Indicators */}
                                                {/*<div className="w-full">*/}
                                                {/*    <div className="flex items-start gap-0 mb-2 sm:mb-3">*/}
                                                {/*        <StepsNavigator*/}
                                                {/*            isExpanded={isExpanded}*/}
                                                {/*            steps={steps}*/}
                                                {/*            part={part}*/}
                                                {/*            onStepClick={(partId, stepId) => {*/}
                                                {/*                setStep(part?.id, stepId);*/}
                                                {/*                if (currentStep == stepId || !isExpanded) {*/}
                                                {/*                    togglePartExpansion(part.id);*/}
                                                {/*                }*/}
                                                {/*            }}*/}
                                                {/*            currentStep={currentStep}*/}
                                                {/*        />*/}
                                                {/*    </div>*/}
                                                {/*</div>*/}

                                                {/*<div className={'mt-4 mb-4'}>*/}
                                                {/*    <Divider />*/}
                                                {/*</div>*/}
                                                {isComplete ? (
                                                    <div
                                                        className="cart-collapsed-prices p-3 rounded-lg border-0"
                                                        style={{
                                                            backgroundColor: '#F7F9FC',
                                                        }}
                                                    >
                                                        {/* Quantity + Lead Time + Total */}
                                                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-2">
                                                            {/* Quantity + Lead Time */}
                                                            <div className="flex flex-col md:flex-row md:items-start gap-4 w-full md:w-auto">
                                                                {/* Quantity */}
                                                                <div className="flex flex-col">
                                                                    <Typography.Text type="secondary">
                                                                        Quantity
                                                                    </Typography.Text>

                                                                    <QuantitySelector
                                                                        quantity={part?.quantity}
                                                                        onQuantitySubmit={quantity => {
                                                                            handleSubmit({
                                                                                id: part.id,
                                                                                quantity,
                                                                            });
                                                                        }}
                                                                        loading={isLoading}
                                                                        min={1}
                                                                    />
                                                                </div>

                                                                {/* Order Frequency – visible on mobile and desktop */}
                                                                <div className="flex flex-col">
                                                                    <FrequencySelector
                                                                        value={part.quantity_frequency || 'one-time'}
                                                                        onChange={value =>
                                                                            handleSubmit({
                                                                                id: part.id,
                                                                                quantity_frequency: value,
                                                                            })
                                                                        }
                                                                        disabled={isLoading}
                                                                    />
                                                                </div>

                                                                {/* Lead Time Options */}
                                                                {isSafeAutoQuote && (
                                                                    <div className="flex flex-col">
                                                                        <LeadTimePriceOptions
                                                                            lineItem={part}
                                                                            direction={
                                                                                isTabletOrLess ? 'column' : 'grid'
                                                                            }
                                                                            handleChange={leadTimeOptionId => {
                                                                                onUpdateItemPrice(
                                                                                    part.id,
                                                                                    leadTimeOptionId
                                                                                );
                                                                            }}
                                                                        />

                                                                        {/*<ShortLeadTimeOptions*/}
                                                                        {/*    leadTimeEntries={leadTimeEntries}*/}
                                                                        {/*    selectedLeadTime={*/}
                                                                        {/*        selectedLeadTime?.id ?? null*/}
                                                                        {/*    }*/}
                                                                        {/*    onUpdateItemPrice={leadTimeOptionId => {*/}
                                                                        {/*        if (leadTimeOptionId) {*/}
                                                                        {/*            onUpdateItemPrice(*/}
                                                                        {/*                part.id,*/}
                                                                        {/*                leadTimeOptionId*/}
                                                                        {/*            );*/}
                                                                        {/*        }*/}
                                                                        {/*    }}*/}
                                                                        {/*    loading={isLoading}*/}
                                                                        {/*/>*/}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Total Price */}
                                                            {isSafeAutoQuote ? (
                                                                <div className="text-left sm:text-right">
                                                                    {/*<div className="text-[10px] sm:text-xs text-slate-500">*/}
                                                                    {/*    {part.quantity} ×{' '}*/}
                                                                    {/*    <Price value={unitPrice.toFixed(2)} />*/}
                                                                    {/*</div>*/}

                                                                    {/*<div className="text-xl sm:text-2xl font-bold leading-tight">*/}
                                                                    {/*    <Price value={totalPrice.toFixed(2)} />*/}
                                                                    {/*</div>*/}
                                                                </div>
                                                            ) : isPricingInProgress ? (
                                                                <div className="text-left sm:text-right self-start md:self-center md:w-2/5">
                                                                    <CalculatingPriceState />
                                                                </div>
                                                            ) : (
                                                                <div className="text-left sm:text-right self-start md:self-center md:w-2/5">
                                                                    <ManualQuoteAlert
                                                                        pricingFeedback={part.customer_pricing_feedback}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <Spin spinning={isLoading} indicator={<LoadingOutlined />}>
                                                        {/* loading */}
                                                    </Spin>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Expandable Step Content – modal on tablet or less, inline on desktop */}
                                {isExpanded &&
                                    (isTabletOrLess ? (
                                        <Modal
                                            open
                                            onCancel={() => togglePartExpansion(part.id)}
                                            footer={null}
                                            width="min(96vw, 720px)"
                                            centered
                                            title={part?.name || 'Configure part'}
                                            bodyStyle={{ maxHeight: '85vh', overflowY: 'auto' }}
                                        >
                                            {modalStepContent}
                                        </Modal>
                                    ) : (
                                        <motion.div
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.12 }}
                                            className="step-content w-full mb-3"
                                        >
                                            <div
                                                className="relative border-slate-200 border border-blue-200 shadow-sm"
                                                style={{
                                                    marginLeft: 20,
                                                    marginRight: 20,
                                                    padding: 10,
                                                    backgroundColor: '#fff',
                                                }}
                                            >
                                                <motion.div
                                                    transition={{ type: 'spring', stiffness: 300 }}
                                                    ref={caretRef}
                                                    className="caret-up absolute top-0 -translate-y-[calc(50%+1px)] w-5 h-5 bg-white border-l border-t border-slate-300 rotate-45 z-20"
                                                />
                                                {stepPanelContent}
                                            </div>
                                        </motion.div>
                                    ))}
                            </>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};

export default observer(PartRow);
